import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { signOut } from "firebase/auth";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { getFirebaseAuth } from "./lib/firebase";
// Side-effect import: starts listening for Chrome's one-shot install event.
import "./lib/installPrompt";
import "./index.css";

// Clear the app-icon badge (set by the push service worker) whenever the app
// is opened or brought back to the foreground — the user is looking at the
// app now, so the "something happened" marker has done its job. Also dismiss
// the delivered notifications so the badge doesn't come back from a stale
// count on the next push.
function clearAppBadge() {
  if (!navigator.clearAppBadge) return;
  navigator.clearAppBadge().catch(() => {});
  navigator.serviceWorker?.ready
    .then((reg) => reg.getNotifications())
    .then((list) => list.forEach((n) => n.close()))
    .catch(() => {});
}
clearAppBadge();
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") clearAppBadge();
});

/**
 * Global handler for 401 errors from any query or mutation. The
 * auth-aware apiFetch tags 401s with `err.status === 401`, so we can
 * detect them centrally instead of every component handling it.
 *
 * What we do on a 401:
 *   1. Sign out of Firebase so any stale session in IndexedDB is
 *      cleared. Without this the user could keep failing in a loop.
 *   2. Drop the React Query cache so previously rendered private data
 *      doesn't flash on the next render.
 *   3. Send them to /login. The page they were on is preserved as
 *      ?from= so the LoginPage already redirects back after sign-in.
 */
function handleUnauthenticated() {
  // Guard against running on the login page itself.
  if (window.location.pathname.startsWith("/login")) return;

  // Best-effort sign-out; failures (Firebase not configured, offline)
  // shouldn't block the redirect.
  try {
    void signOut(getFirebaseAuth()).catch(() => {});
  } catch {
    // Firebase not configured.
  }
  queryClient.clear();
  const fromPath = window.location.pathname + window.location.search;
  window.location.assign(`/login?from=${encodeURIComponent(fromPath)}`);
}

function isUnauthenticated(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    (err as { status?: unknown }).status === 401
  );
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (isUnauthenticated(err)) handleUnauthenticated();
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (isUnauthenticated(err)) handleUnauthenticated();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retry transient errors but not 401s — auth failures don't fix
      // themselves on retry, and waiting for retries delays the
      // /login redirect.
      retry: (failureCount, err) => {
        if (isUnauthenticated(err)) return false;
        return failureCount < 1;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
